// ============================================================================
// notification-policy.js — single source of truth for which transient
// notifications are shown (Peter 2026-06-27)
// ----------------------------------------------------------------------------
// The AI-pipeline PROGRESS chatter (the top "AI is running: X" banner + the
// bottom-right "Saving…", "Considering alternatives…", "Writing work
// methods…" toasts) and the COMPLETION confirmations ("Project saved", "Risk
// analysis complete", "Work Method ready", "Marked as WON"…) were judged noise
// and switched off here. Two surfaces consult this module:
//
//   1. The TOP "AI is running" banner  — App.jsx mounts <LivePipelineBanner>
//      only when isPipelineBannerEnabled() is true.
//   2. The BOTTOM-RIGHT toast stack    — App.jsx (the global setToast state)
//      and the per-component useToast({ suppressProgress: true }) consumers
//      (Stage 5 FullQuoteEditorModal, Stage 2) gate each toast through
//      shouldShowToast().
//
// FAILURES ALWAYS SHOW. Only `info` (progress) and `success` (completion)
// toasts are suppressed; `warning` and `error` (and any other type) pass
// through untouched, because they are the only signal a user gets when a save
// or AI call fails. A short keep-list rescues the handful of info/success
// notices that are "you're blocked / can't proceed" guards rather than chatter
// (concurrency guards, the session-expiry notice, the strategy-stayed-locked
// explanation).
//
// To RE-ENABLE a category: flip the relevant flag below (banner) or remove a
// type from SUPPRESSED_TOAST_TYPES (toasts). To rescue one more info/success
// message, add a substring to KEEP_TOAST_PATTERNS. Tunable in one file by
// design — nothing else needs to change.
// ============================================================================

// TOP "AI is running: X" pipeline banner. false = hidden. (Peter 2026-06-27)
export const PIPELINE_BANNER_ENABLED = false;

export interface ToastNotificationCandidate {
    message?: any;
    type?: string;
    [key: string]: any;
}

// Bottom-right toast TYPES that are suppressed by the progress/completion
// policy. `warning` + `error` are intentionally absent — failures always show.
export const SUPPRESSED_TOAST_TYPES: Set<string> = new Set(['info', 'success']);

// info/success messages that should STILL show despite the policy, matched as
// case-insensitive substrings. These are blocked/guard notices + the auth
// session notice, NOT progress/completion chatter.
export const KEEP_TOAST_PATTERNS: string[] = [
    'in flight',          // "…already in flight — please wait" concurrency guards
    'please wait',        // belt-and-braces for the same guards
    'no completed quote', // "No completed quote to view yet — generate one first."
    'session expired',    // "Your session expired — please log in again."
    'stayed locked',      // "Strategy stayed locked on … Unlock via the Strategy panel"
    // "Reopening the project this tab last had open…" and "…was not reopened,
    // because you moved on… Open QuoteMaker to reopen it." (useAppRouting,
    // 2026-09-29): after a reload the Run Sheet shows while the job reopens,
    // and these are the only sign of it and of what to do.
    'this tab last had open',
    // "Still opening project … QuoteMaker shows it as soon as it has loaded."
    // (useAppRouting, 2026-09-30): Menu > QuoteMaker and Back wait while an
    // Open in QuoteMaker loads, and this is the only sign they did anything.
    'still opening project',
    // Stage 3 Next (use-stage-navigation, 2026-10-20): "Preparing Scope
    // Builder…", "Still preparing Scope Builder…" and "Scope Builder is
    // ready…". Preparing Stage 4 links photos with an AI call and saves, which
    // can take a minute; without these the Next button looked dead
    // ("the Next button from Stage 3 doesn't do anything", Peter).
    'preparing scope builder',
    'scope builder is ready',
    // Outage lane (2026-09-29): edits kept on this device while QuoteMaker's
    // server was not answering were painted back into a project, and the
    // confirmation that they saved once it answered. Without these the
    // project is unsaved for no visible reason and the banner just vanishes.
    'kept on this device',
    'back online',
    // Quote Brain (slice WRITE, 2026-10-26): an old writer refused on a brain
    // project ("The Quote Brain wrote this quote, so … is not available"), the
    // run writing the quote, and a Stage 4 price check by pricing-core. Without
    // these a refused Generate or Regenerate button looks dead.
    'quote brain',
    'pricing-core',
];

/**
 * Whether the top "AI is running" pipeline banner should mount.
 * @returns {boolean}
 */
export function isPipelineBannerEnabled(): boolean {
    return PIPELINE_BANNER_ENABLED;
}

/**
 * Decide whether a toast should be shown under the current notification policy.
 * Failures (warning/error/anything not in SUPPRESSED_TOAST_TYPES) always show.
 * Progress (info) + completion (success) are suppressed unless the message
 * matches a KEEP_TOAST_PATTERNS substring.
 *
 * @param {{ message?: string, type?: string }|null|undefined} toast
 * @returns {boolean}
 */
export function shouldShowToast(toast?: ToastNotificationCandidate | null): boolean {
    if (!toast) return false;
    const type = toast.type || 'info';
    if (!SUPPRESSED_TOAST_TYPES.has(type)) return true; // warning / error / neutral
    const msg = String(toast.message || '').toLowerCase();
    return KEEP_TOAST_PATTERNS.some((pattern: string) => msg.includes(pattern));
}
